import { Skeleton } from "@chakra-ui/react";
import { handleError, httpStatusCodes } from "@metriport/shared-internal";
import dayjs from "dayjs";
import { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setAuthTokenHeaders } from "../../../api/api";
import { getMetriportPatientId as athenaGetMetriportPatientId } from "../../../api/ehr/athena";
import { getMetriportPatientId as canvasGetMetriportPatientId } from "../../../api/ehr/canvas";
import { getMetriportPatientId as eclinicalworksGetMetriportPatientId } from "../../../api/ehr/eclinicalworks";
import { getMetriportPatientId as elationGetMetriportPatientId } from "../../../api/ehr/elation";
import { getMetriportPatientId as healthieGetMetriportPatientId } from "../../../api/ehr/healthie";
import { getMetriportPatientId as salesforceGetMetriportPatientId } from "../../../api/ehr/salesforce";
import { getMetriportPatientId as practicefusionGetMetriportPatientId } from "../../../api/ehr/practicefusion";
import { getDashboardFeatureFlagsForUser } from "../../../api/feature-flags";
import {
  athenaHealthAuthPath,
  authPaths,
  authPathSchema,
  canvasAuthPath,
  eclinicalworksAuthPath,
  elationAuthPath,
  healthieAuthPath,
  salesforceAuthPath,
  practicefusionAuthPath,
} from "../../../domain/ehr";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";
import { AppState, AppStateAction, AppStateActionType, initialState, reducer } from "./reducer";

const MISSING_PARAMS_ERROR_TITLE = "Missing required parameters. Please try re-opening the app.";
const MISSING_PARAMS_ERROR_DURATION = dayjs.duration(25, "second").asMilliseconds();

interface IAppStateContext {
  state: AppState;
  dispatch: Dispatch<AppStateAction>;
}

export const AppStateContext = createContext<IAppStateContext>({
  state: initialState,
  dispatch: () => null,
});

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const toast = useMetriportToast();

  const path = location.pathname;
  const initializePath = authPaths.includes(path);

  useEffect(() => {
    async function fetchInitialState() {
      if (!initializePath) return;
      try {
        const hash = location.hash;
        const parsedHash = new URLSearchParams(hash.substring(1));
        // Token
        const token = parsedHash.get("access_token");
        navigate(path, { replace: true });
        if (!token) {
          toast.error({
            title: MISSING_PARAMS_ERROR_TITLE,
            duration: MISSING_PARAMS_ERROR_DURATION,
          });
          return;
        }
        const ehrAuthPath = authPathSchema.parse({ authPath: path });
        setAuthTokenHeaders(token, ehrAuthPath);
        // Patient
        const patient = parsedHash.get("patient");
        if (!patient) {
          toast.error({
            title: MISSING_PARAMS_ERROR_TITLE,
            duration: MISSING_PARAMS_ERROR_DURATION,
          });
          return;
        }
        let practitionerId: string | undefined;
        if (ehrAuthPath.authPath === athenaHealthAuthPath) {
          await athenaGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === canvasAuthPath) {
          practitionerId = parsedHash.get("practitioner") ?? undefined;
          await canvasGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === elationAuthPath) {
          await elationGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === healthieAuthPath) {
          await healthieGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === eclinicalworksAuthPath) {
          await eclinicalworksGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === salesforceAuthPath) {
          await salesforceGetMetriportPatientId(patient);
        } else if (ehrAuthPath.authPath === practicefusionAuthPath) {
          await practicefusionGetMetriportPatientId(patient);
        }
        const featureFlags = await getDashboardFeatureFlagsForUser();
        dispatch({
          type: AppStateActionType.update,
          newState: {
            ehrPatientId: patient,
            ehrPractitionerId: practitionerId,
            authToken: token,
            isLoaded: true,
            featureFlags,
          },
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const { handled } = handleError({
          error,
          errorActionsMap: {
            [httpStatusCodes.BAD_REQUEST]: {
              path: "/patient-bad-request",
              toast: "The EHR patient could not be processed",
            },
            [httpStatusCodes.NOT_FOUND]: {
              path: "/patient-not-found",
              toast: "The EHR patient was not found",
            },
          },
          toast,
          navigate,
          includeDefaultActions: true,
        });
        if (!handled) {
          captureAndDisplayError({
            error,
            msg: "Failed to initialize the application, please reach out to support@metriport.com",
            context: "fetchInitialState",
            captureMsg: "Component: AppStateProvider - initializing ehr dash state failed",
            toast,
          });
        }
      }
    }
    fetchInitialState();
  }, []);

  // Prevent that the UI/components are rendered before set the auth token
  if (initializePath && !state.isLoaded) return <Skeleton height="100vh" width="100vw" />;

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
  );
};

export const useAppContext = () => useContext(AppStateContext);
